import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSlackDraft, useSlackReply, useSlackThread } from '../hooks/use-slack.js';
import { Button } from './button.js';
import { PlainText } from './plain-text.js';
import { SetAsideForm } from './set-aside-form.js';
import { timeAgo } from '../utils/time-ago.js';

// One Slack conversation or thread aidev kept (ADR-039 step 8): who wrote what, the asks marked, then the reply. A
// draft comes on demand only, in the operator's voice; the reply goes under his name, in the conversation the ask came
// from, on his click. To do opens it.

const field = 'block w-full rounded-md px-2.5 py-1.5 text-sm font-[inherit] leading-relaxed focus:outline-none focus-visible:outline focus-visible:outline-2';
const fieldStyle = { backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' };
/** How long Send stays held after Slack did not answer in time: the message may be there already */
export const UNSURE_HOLD_MS = 60_000;

export function SlackThreadPage({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { group: raw } = useParams<{ group: string }>();
  // The browser's router leaves an encoded « : » as it is
  const group = (() => { try { return decodeURIComponent(raw ?? ''); } catch { return raw ?? ''; } })();
  const { data, error, isLoading } = useSlackThread(serverId, group);
  const draft = useSlackDraft(serverId, group);
  const reply = useSlackReply(serverId, group);
  const [text, setText] = useState('');
  const [aside, setAside] = useState(false);
  const unsure = !!reply.data && 'unsure' in reply.data;
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (!held) return;
    const t = setTimeout(() => setHeld(false), UNSURE_HOLD_MS);
    return () => clearTimeout(t);
  }, [held]);
  const back = <Link to="/" className="text-xs hover:underline" style={{ color: 'var(--color-brand-light)' }}>To do</Link>;
  if (isLoading) return <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>Reading the conversation…</p>;
  if (error || !data) return <p role="alert" className="text-sm" style={{ color: 'var(--color-text-2)' }}>{(error as Error | null)?.message ?? 'Not found'}. {back}</p>;
  const last = data.messages.at(-1)!;
  const asked = data.messages.filter((m) => m.triage?.class === 'ask').at(-1) ?? last;
  const where = asked.kind === 'im' ? `a direct message with ${asked.userName ?? asked.user}` : asked.kind === 'mpim' ? 'a group message' : `#${asked.channelName ?? 'a channel'}${data.reply.threadTs ? ', in a thread' : ''}`;
  const slackLink = asked.permalink ?? `https://app.slack.com/client/${data.teamId}/${asked.channel}`;
  return (
    <div className="space-y-3">
      {back}
      <article aria-labelledby="slack-title" className="card min-w-0 px-4 py-4 hover:transform-none sm:px-5">
        <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h3 id="slack-title" className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>Slack: {where}</h3>
          <a href={slackLink} target="_blank" rel="noreferrer" className="text-xs hover:underline" style={{ color: 'var(--color-brand-light)' }}>
            Open in Slack<span className="sr-only"> (new tab)</span>
          </a>
        </header>
        <ol className="mt-3 space-y-3" aria-label="The messages aidev kept">
          {data.messages.map((m) => (
            <li key={m.key} className="max-w-[72ch] border-l-2 pl-3" style={{ borderColor: m.triage?.class === 'ask' ? 'var(--color-warn)' : 'var(--color-border)' }}>
              <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>
                <span style={{ color: 'var(--color-text-2)' }}>{m.userName ?? m.user}</span>{m.bot && ' (app)'}, {timeAgo(m.receivedAt)}
                {m.triage?.class === 'ask' && <span style={{ color: 'var(--color-warn)' }}>: asks you{m.triage.urgent ? ', urgently' : ''}</span>}
              </p>
              <PlainText text={m.text} className="mt-0.5 text-sm" />
              {/* What was shared or forwarded into it: what the ask points at */}
              {m.shared && (
                <blockquote className="mt-1.5 border-l-2 pl-2.5" style={{ borderColor: 'var(--color-border-2)' }} aria-label="Shared into this message">
                  <PlainText text={m.shared} className="break-words text-xs" color="var(--color-text-2)" />
                </blockquote>
              )}
            </li>
          ))}
        </ol>
        <p className="mt-2 text-xs" style={{ color: 'var(--color-text-3)' }}>Only what is yours is kept: direct and group messages, and channel messages that mention you, 14 days.</p>

        <div className="mt-4 space-y-2 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
          {reply.isSuccess && !unsure ? (
            <p className="text-sm" style={{ color: 'var(--color-success)' }}>Sent on Slack under your name. The line waits for an answer.</p>
          ) : isAdmin ? (
            <form className="max-w-[72ch] space-y-2" aria-label="Your reply on Slack" onSubmit={(e) => { e.preventDefault(); reply.mutate(text.trim(), { onSuccess: (d) => setHeld('unsure' in d) }); }}>
              <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
                Your reply
                {/* 4000: MAX_REPLY in src/services/slack-reply.ts */}
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={4000} className={`${field} mt-1`} style={fieldStyle} />
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" size="sm" className="max-lg:min-h-10" disabled={!text.trim() || reply.isPending || held}>{reply.isPending ? 'Sending…' : 'Send on Slack'}</Button>
                <Button type="button" size="sm" variant="ghost" className="max-lg:min-h-10" disabled={draft.isPending}
                  onClick={() => draft.mutate(undefined, { onSuccess: (d) => setText((t) => (t.trim() ? `${t.trimEnd()}\n\n${d.draft}` : d.draft)) })}>
                  {draft.isPending ? 'Preparing…' : 'Prepare a reply'}
                </Button>
                <Button type="button" size="sm" variant="ghost" className="max-lg:min-h-10" onClick={() => setAside(!aside)} aria-expanded={aside}>Set aside</Button>
              </div>
              <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Sent under your name, in this conversation{data.reply.threadTs ? '’s thread' : ''}, when you click. A prepared reply is only a draft until then.</p>
              {draft.error && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>No draft: {(draft.error as Error).message}</p>}
              {unsure && (
                <p role="alert" className="text-xs" style={{ color: 'var(--color-warn)' }}>
                  Slack did not answer in time: the reply may be there already. Check the conversation
                  (<a href={slackLink} target="_blank" rel="noreferrer" className="underline">open in Slack</a>) before sending again{held ? '; Send is held for a minute' : ''}.
                </p>
              )}
              {reply.error && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not sent: {(reply.error as Error).message}</p>}
            </form>
          ) : (
            <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Only an admin can reply from aidev. Answer in Slack instead.</p>
          )}
          {aside && <SetAsideForm serverId={serverId} itemId={`slack:${group}`} onDone={() => setAside(false)} onCancel={() => setAside(false)} />}
        </div>
      </article>
    </div>
  );
}
